import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {selectThemeMode} from "./themeSlice";

const ThemeContext = createContext({mode: "system", resolvedMode: "light", setResolvedMode: () => {}});

export const useTheme = () => useContext(ThemeContext);

const getSystemMode = () => {
    if (typeof window === "undefined" || !window.matchMedia) {
        return "light";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

/**
 * Применяет тему к <html data-theme="..."> и следит за системной темой.
 */
const ThemeProvider = ({children}) => {
    const mode = useSelector(selectThemeMode);
    const [systemMode, setSystemMode] = useState(getSystemMode);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) {
            return undefined;
        }
        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (event) => setSystemMode(event.matches ? "dark" : "light");
        media.addEventListener("change", handler);
        return () => media.removeEventListener("change", handler);
    }, []);

    const resolvedMode = mode === "system" ? systemMode : mode;

    useEffect(() => {
        const root = document.documentElement;
        root.dataset.theme = resolvedMode;
        root.style.colorScheme = resolvedMode;

        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) {
            meta.setAttribute("content", resolvedMode === "dark" ? "#0a111d" : "#ffffff");
        }
    }, [resolvedMode]);

    const value = useMemo(() => ({mode, resolvedMode, setResolvedMode: setSystemMode}), [mode, resolvedMode]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
